import { Component, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClientService } from 'app/services/client-service';
import { PolicyService } from 'app/services/policy-service';
import { ChatService } from 'app/services/chat-service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'app/security/auth.service';
import { CommonsText } from 'app/commons/CommonsText';
import { ClientDataDTO } from 'app/header-footer-template/model/ClientDataDTO';
import { ProfileOption } from '../../components/profile/profile-sidebar/profile-sidebar.component';
import { environment } from 'environments/environment';
import { CommonsUtils } from 'app/commons/CommonsUtils';

@Component({
    selector: 'app-profile-page',
    templateUrl: './profile-page.component.html',
    styleUrls: ['./profile-page.component.scss']
})
export class ProfilePageComponent implements OnInit, AfterViewInit, OnDestroy {

    // Arrancan en true porque ngAfterViewInit es quien lanza las cargas: si empezaran en false,
    // la vista ya se habria comprobado con ese valor y el cambio a true dentro del hook provoca
    // un ExpressionChangedAfterItHasBeenCheckedError sobre el *ngIf del "Cargando...".
    loadingProfile: Boolean = true;
    loadingPayment: Boolean = true;
    profileResponse: any;
    paymentResponse: any;
    showDirectAreas: Boolean = true;
    isTaker: boolean = false;
    isCompany: boolean = false;

    client: ClientDataDTO;
    roadTypeList: any[] = [];

    activeOption: ProfileOption = 'profile';
    /**
     * Respaldo mientras no han llegado los datos de cliente: el nombre del token, que se fija
     * al iniciar sesion.
     */
    tokenFullName: string = '';
    userEmail: string = '';
    editing: boolean = false;

    hasLegalAssistanceProduct: Boolean = false;

    private sectionSub: Subscription;

    constructor(private clientService: ClientService,
                private policyService: PolicyService,
                private chatService: ChatService,
                private toastr: ToastrService,
                private authService: AuthService,
                private route: ActivatedRoute,
                private renderer: Renderer2,
                @Inject(DOCUMENT) private document: Document) { }

    ngOnInit() {
        if (this.authService.isLogged()) {
            this.showDirectAreas = this.authService.getSelectedEntity() === 'ONLYGAL';
            this.loadTakerSummary();
            this.loadLegalAssistanceProduct();
        }

        const u = this.authService.getUser();
        if (u) {
            this.tokenFullName = ((u.name || '') + ' ' + (u.lastName || '')).trim();
            this.userEmail = (u as any).email || '';
            this.isCompany = !!(u.documentType && u.documentType === 'CIF');
        }

        // Suscrito y no leido del snapshot: por debajo de $md la barra lateral solo muestra nombre y correo,
        // asi que se cambia de seccion desde el menu de usuario de la cabecera, que navega a
        // /profile con otro ?section= y Angular reutiliza este mismo componente sin volver a pasar
        // por ngOnInit.
        this.sectionSub = this.route.queryParamMap.subscribe(params => {
            const section = params.get('section') as ProfileOption;
            if (section) {
                this.activeOption = section;
            }
        });

        this.clientService.getRoadTypes()
            .then(value => {
                this.roadTypeList = value.response;
            })
            .catch(() => {
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_TIPO_D_VIA, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    /**
     * Se compone de los datos de cliente y no del token: asi incluye el primer apellido y se
     * refresca solo al editar el perfil, porque this.client es el mismo objeto que ClientService
     * actualiza al guardar.
     */
    get userFullName(): string {
        return CommonsUtils.fullName(this.client) || this.tokenFullName;
    }

    ngAfterViewInit() {
        this.loadingProfile = true;
        this.loadingPayment = true;

        this.clientService.getClient()
            .then(success => {
                this.loadingProfile = false;
                this.profileResponse = success;
                this.client = success;
                if (success && (success as any).email && !this.userEmail) {
                    this.userEmail = (success as any).email;
                }
            })
            .catch(() => {
                this.loadingProfile = false;
                this.profileResponse = null;
                this.client = null;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_CLIENTE, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });

        this.clientService.getPayment()
            .then(success => {
                this.loadingPayment = false;
                this.paymentResponse = success.response;
            })
            .catch(() => {
                this.loadingPayment = false;
                this.paymentResponse = null;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_CLIENTE, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    /** Relación del cliente con sus pólizas; la barra lateral la usa para "Consentimiento". */
    private loadTakerSummary(): void {
        this.policyService.isTaker()
            .then(is => { this.isTaker = is; });
    }

    private loadLegalAssistanceProduct(): void {
        this.policyService.hasLegalAssistanceProduct()
            .then(has => {
                this.hasLegalAssistanceProduct = has;
                if (has) {
                    this.chatService.ensureWidgetLoaded();
                }
            })
            .catch(() => {
                this.hasLegalAssistanceProduct = false;
            });
    }

    ngOnDestroy() {
        if (this.sectionSub) {
            this.sectionSub.unsubscribe();
        }
    }

    onOptionChange(option: ProfileOption): void {
        this.activeOption = option;
    }

    onEdit(): void {
        this.editing = true;
    }

    onEditClose(): void {
        this.editing = false;
    }
}
